import { useEffect, useRef, useState } from "react";
import {
  Button,
  Card,
  DatePicker,
  Icon,
  Input,
  Modal,
  Space,
  Spin,
  Table,
  Text,
  UploadButton,
  useToast,
} from "@app/ui";
import {
  createCertification,
  deleteCertification,
  deleteCertificationImage,
  fetchCertificationImageRaw,
  listCertifications,
  updateCertification,
  uploadCertificationImage,
} from "../api/dpp.js";
import type { Certification, CertificationBody } from "../api/dpp.js";
import { useAsync } from "../api/useAsync.js";
import { ErrorAlert, useErrorText } from "../errors/index.js";
import { useT } from "../i18n.js";
import { certValidity } from "./certValidity.js";

/** Server-side rules mirrored for fast client feedback (the API re-enforces both). */
const IMAGE_ACCEPT = "image/jpeg,image/png,image/webp";
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

interface CertDraft {
  scheme: string;
  certificateNo: string;
  issuer: string;
  validFrom: string;
  validUntil: string;
  evidenceUrl: string;
}

const EMPTY_CERT: CertDraft = {
  scheme: "",
  certificateNo: "",
  issuer: "",
  validFrom: "",
  validUntil: "",
  evidenceUrl: "",
};

function certBody(d: CertDraft): CertificationBody {
  return {
    scheme: d.scheme.trim(),
    ...(d.certificateNo.trim() ? { certificateNo: d.certificateNo.trim() } : {}),
    ...(d.issuer.trim() ? { issuer: d.issuer.trim() } : {}),
    ...(d.validFrom ? { validFrom: d.validFrom } : {}),
    ...(d.validUntil ? { validUntil: d.validUntil } : {}),
    ...(d.evidenceUrl.trim() ? { evidenceUrl: d.evidenceUrl.trim() } : {}),
  };
}

const THUMB_STYLE = {
  objectFit: "contain",
  borderRadius: "var(--az-radius, 6px)",
  border: "1px solid var(--az-border, #ddd)",
} as const;

/** The org's certificates with thumbnails, and the create/edit dialog incl. the image. */
export function CertificationsCard({ orgId, canWrite }: { orgId: string; canWrite: boolean }) {
  const t = useT();
  const toast = useToast();
  const errorText = useErrorText();
  const { data: certs, error, loading, reload } = useAsync(() => listCertifications(orgId), [orgId]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [draft, setDraft] = useState<CertDraft>(EMPTY_CERT);
  const [saving, setSaving] = useState(false);
  /** Image picked in the modal, uploaded only after the certificate is saved. */
  const [imagePick, setImagePick] = useState<{ file: File; url: string } | null>(null);
  const [imageRemoved, setImageRemoved] = useState(false);

  // Thumbnails go through the authenticated raw route (blob object-URLs) — the
  // public /media/cert/ URL only resolves once a passport referencing the
  // certificate is published. Keyed by media id, so replacing an image
  // invalidates the cached blob by construction.
  const [thumbs, setThumbs] = useState<Record<string, string>>({});
  const thumbsRef = useRef<Record<string, string>>({});

  useEffect(() => {
    if (!certs) return;
    let live = true;
    const wanted = new Set(certs.flatMap((c) => (c.image ? [c.image.id] : [])));
    for (const [mediaId, url] of Object.entries(thumbsRef.current)) {
      if (wanted.has(mediaId)) continue;
      URL.revokeObjectURL(url);
      delete thumbsRef.current[mediaId];
      setThumbs((prev) => {
        const next = { ...prev };
        delete next[mediaId];
        return next;
      });
    }
    void (async () => {
      for (const cert of certs) {
        const image = cert.image;
        if (!image || thumbsRef.current[image.id]) continue;
        try {
          const url = URL.createObjectURL(await fetchCertificationImageRaw(orgId, cert.id));
          if (!live) {
            URL.revokeObjectURL(url);
            return;
          }
          thumbsRef.current[image.id] = url;
          setThumbs((prev) => ({ ...prev, [image.id]: url }));
        } catch {
          // A thumbnail is cosmetic — a failed fetch just leaves the cell empty.
        }
      }
    })();
    return () => {
      live = false;
    };
  }, [certs, orgId]);

  useEffect(() => {
    const urls = thumbsRef.current;
    return () => {
      for (const url of Object.values(urls)) URL.revokeObjectURL(url);
      thumbsRef.current = {};
    };
  }, []);

  const clearImagePick = () => {
    setImagePick((prev) => {
      if (prev) URL.revokeObjectURL(prev.url);
      return null;
    });
  };

  const openModal = (cert?: Certification) => {
    clearImagePick();
    setImageRemoved(false);
    setEditId(cert?.id ?? null);
    setDraft(
      cert
        ? {
            scheme: cert.scheme,
            certificateNo: cert.certificateNo ?? "",
            issuer: cert.issuer ?? "",
            validFrom: cert.validFrom ? cert.validFrom.slice(0, 10) : "",
            validUntil: cert.validUntil ? cert.validUntil.slice(0, 10) : "",
            evidenceUrl: cert.evidenceUrl ?? "",
          }
        : EMPTY_CERT,
    );
    setOpen(true);
  };

  const pickImage = (file: File) => {
    if (file.size > MAX_IMAGE_BYTES) {
      toast.error(t("media.tooLarge"));
      return;
    }
    clearImagePick();
    setImagePick({ file, url: URL.createObjectURL(file) });
    setImageRemoved(false);
  };

  const dropImage = () => {
    clearImagePick();
    setImageRemoved(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const saved = editId
        ? await updateCertification(orgId, editId, certBody(draft))
        : await createCertification(orgId, certBody(draft));
      toast.success(t("certs.saved"));
      // The image rides on the saved certificate, so it can only go up once the
      // row exists — a failure here must not undo the save the user just got.
      try {
        if (imagePick) await uploadCertificationImage(orgId, saved.id, imagePick.file);
        else if (imageRemoved && saved.image) await deleteCertificationImage(orgId, saved.id);
      } catch (e) {
        toast.error(`${t("certs.imageFailed")} ${errorText(e)}`);
      }
      clearImagePick();
      setImageRemoved(false);
      setOpen(false);
      reload();
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: string) => {
    try {
      await deleteCertification(orgId, id);
      toast.success(t("certs.deleted"));
      reload();
    } catch (e) {
      toast.error(errorText(e));
    }
  };

  const edited = editId === null ? null : ((certs ?? []).find((c) => c.id === editId) ?? null);
  const imageUrl =
    imagePick?.url ?? (imageRemoved || !edited?.image ? null : (thumbs[edited.image.id] ?? null));

  return (
    <Card
      title={t("certs.title")}
      extra={
        canWrite && (
          <Button variant="primary" icon={<Icon name="plus" size={16} />} onClick={() => openModal()}>
            {t("certs.new")}
          </Button>
        )
      }
    >
      {error != null ? (
        <ErrorAlert error={error} onRetry={reload} />
      ) : loading && !certs ? (
        <Spin />
      ) : (
        <Table
          rowKey={(c) => c.id}
          empty={t("certs.empty")}
          columns={[
            {
              key: "image",
              title: t("certs.image"),
              render: (c) =>
                c.image && thumbs[c.image.id] ? (
                  <img src={thumbs[c.image.id]} alt="" style={{ width: 44, height: 44, ...THUMB_STYLE }} />
                ) : (
                  "—"
                ),
            },
            { key: "scheme", title: t("certs.scheme"), render: (c) => c.scheme },
            { key: "no", title: t("certs.no"), render: (c) => c.certificateNo ?? "—" },
            { key: "issuer", title: t("certs.issuer"), render: (c) => c.issuer ?? "—" },
            {
              key: "validUntil",
              title: t("certs.validUntil"),
              render: (c) => certValidity(c, t("certs.noExpiry")),
            },
            {
              key: "actions",
              title: t("common.actions"),
              render: (c) =>
                canWrite ? (
                  <Space>
                    <Button size="sm" onClick={() => openModal(c)}>
                      {t("common.edit")}
                    </Button>
                    <Button size="sm" variant="danger" onClick={() => void remove(c.id)}>
                      {t("common.remove")}
                    </Button>
                  </Space>
                ) : null,
            },
          ]}
          data={certs ?? []}
        />
      )}

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={editId ? t("certs.edit") : t("certs.new")}
        onOk={() => void save()}
        okText={t("common.save")}
        cancelText={t("common.cancel")}
        confirmLoading={saving}
        width={520}
      >
        <Space direction="vertical" size="md" style={{ width: "100%" }}>
          <Input
            label={t("certs.scheme")}
            required
            placeholder="GOTS"
            value={draft.scheme}
            onChange={(e) => setDraft({ ...draft, scheme: e.target.value })}
          />
          <Space wrap>
            <Input
              label={t("certs.no")}
              value={draft.certificateNo}
              onChange={(e) => setDraft({ ...draft, certificateNo: e.target.value })}
            />
            <Input
              label={t("certs.issuer")}
              value={draft.issuer}
              onChange={(e) => setDraft({ ...draft, issuer: e.target.value })}
            />
          </Space>
          <Space wrap>
            <DatePicker
              label={t("certs.validFrom")}
              allowClear
              value={draft.validFrom}
              onChange={(v) => setDraft({ ...draft, validFrom: v })}
            />
            <DatePicker
              label={t("certs.validUntil")}
              allowClear
              value={draft.validUntil}
              onChange={(v) => setDraft({ ...draft, validUntil: v })}
            />
          </Space>
          <Input
            label={t("certs.evidenceUrl")}
            placeholder="https://…"
            value={draft.evidenceUrl}
            onChange={(e) => setDraft({ ...draft, evidenceUrl: e.target.value })}
          />
          <div>
            <Text strong>{t("certs.image")}</Text>
            <div style={{ display: "flex", alignItems: "flex-start", gap: "var(--az-sp-3)", marginTop: 4 }}>
              {imageUrl && <img src={imageUrl} alt="" style={{ width: 88, height: 88, ...THUMB_STYLE }} />}
              <Space direction="vertical" size="sm">
                <Space wrap>
                  <UploadButton
                    accept={IMAGE_ACCEPT}
                    size="sm"
                    icon={<Icon name="plus" size={14} />}
                    onSelect={pickImage}
                  >
                    {imageUrl ? t("certs.imageReplace") : t("certs.imageUpload")}
                  </UploadButton>
                  {imageUrl && (
                    <Button size="sm" variant="danger" onClick={dropImage}>
                      {t("certs.imageRemove")}
                    </Button>
                  )}
                </Space>
                <Text secondary>{imagePick ? t("certs.imagePending") : t("certs.imageHint")}</Text>
              </Space>
            </div>
          </div>
        </Space>
      </Modal>
    </Card>
  );
}
